/**
 * Estadísticas agregadas del álbum para el Resumen visual.
 *
 * Da dos vistas complementarias:
 *   - Por grupo (A-L + secciones especiales): cuánto llevas en cada uno.
 *     Sirve para detectar grupos "estancados" donde te faltan muchas.
 *   - Por tipo (jugador / escudo / foto equipo / especial): rompe la
 *     ilusión de "me faltan 50" mostrando QUÉ tipo te falta.
 *
 * Todo puro, sin depender de React, para poder testear sin DOM.
 */

const TYPE_LABELS = {
  player: 'Jugadores',
  badge: 'Escudos',
  team_photo: 'Fotos equipo',
  special: 'Especiales',
};

const TYPE_ORDER = ['player', 'badge', 'team_photo', 'special'];

/**
 * Cuenta cuántas láminas de un set están en `collected` (truthy).
 * Considera `duplicates[number] > 0` como "tenida" porque a veces el
 * usuario marca duplicado sin marcar la pegada primaria — defensivo.
 */
function countHave(stickers, collected, duplicates) {
  let have = 0;
  for (const s of stickers) {
    if (collected?.[s.number] || (duplicates?.[s.number] || 0) > 0) have++;
  }
  return have;
}

function countDuplicates(stickers, duplicates) {
  let dup = 0;
  for (const s of stickers) {
    dup += duplicates?.[s.number] || 0;
  }
  return dup;
}

/**
 * Bucket key: para stickers de grupo usa el `group` (A-L). Para
 * stickers de sección (intro, museum, legends...) usa `section` con
 * prefijo `__` para evitar colisión con grupos.
 */
function bucketKey(sticker) {
  if (sticker.group) return sticker.group;
  if (sticker.section) return `__${sticker.section}`;
  return '__other';
}

function bucketLabel(sticker) {
  if (sticker.groupName) return sticker.groupName;
  if (sticker.sectionName) return sticker.sectionName;
  return 'Otros';
}

export function computeGroupStats(allStickers = [], collected = {}, duplicates = {}) {
  const buckets = new Map();
  for (const s of allStickers) {
    const key = bucketKey(s);
    if (!buckets.has(key)) {
      buckets.set(key, { id: key, name: bucketLabel(s), stickers: [], isSection: key.startsWith('__') });
    }
    buckets.get(key).stickers.push(s);
  }

  const result = [];
  for (const bucket of buckets.values()) {
    const total = bucket.stickers.length;
    const have = countHave(bucket.stickers, collected, duplicates);
    const dup = countDuplicates(bucket.stickers, duplicates);
    result.push({
      id: bucket.id,
      name: bucket.name,
      isSection: bucket.isSection,
      total,
      have,
      missing: total - have,
      duplicates: dup,
      pct: total > 0 ? Math.round((have / total) * 100) : 0,
    });
  }

  // Ordenar: secciones primero (intro, museo, etc.), luego grupos A-L
  // alfabéticos. Dentro de cada grupo, mantener el orden de aparición.
  result.sort((a, b) => {
    if (a.isSection !== b.isSection) return a.isSection ? -1 : 1;
    return a.id.localeCompare(b.id);
  });
  return result;
}

export function computeTypeStats(allStickers = [], collected = {}, duplicates = {}) {
  const buckets = {};
  for (const t of TYPE_ORDER) buckets[t] = { type: t, label: TYPE_LABELS[t], stickers: [] };

  for (const s of allStickers) {
    const t = s.type || 'special';
    if (!buckets[t]) buckets[t] = { type: t, label: TYPE_LABELS[t] || t, stickers: [] };
    buckets[t].stickers.push(s);
  }

  return Object.values(buckets)
    .filter(b => b.stickers.length > 0)
    .map(b => {
      const have = countHave(b.stickers, collected, duplicates);
      return {
        type: b.type,
        label: b.label,
        total: b.stickers.length,
        have,
        missing: b.stickers.length - have,
        pct: b.stickers.length > 0 ? Math.round((have / b.stickers.length) * 100) : 0,
      };
    });
}

export const __internals = { TYPE_LABELS, TYPE_ORDER, bucketKey, bucketLabel };
